import { createPortal } from "react-dom";
import PaymentInstructionLayout from "../layouts/PaymentInstructionLayout";
import { useState, useEffect, useCallback, useRef } from "react";
import { CreditCard, Lock, AlertCircle, Eye, EyeOff } from "lucide-react";
import {
  formatCardNumber,
  formatExpiry,
  parseExpiryToYyMm,
  detectCardBrand,
  validateCardNumber,
  validateExpiry,
  validateCVV,
  isCardBrandSupported,
} from "../../paymentLinking/utils/cardUtils";
import { cardLogos } from "../../paymentLinking/utils/cardLogos";
import { getPaymentLinkHeaders } from "../../utils/paymentLinkHeaders";

const BASE_URL = process.env.REACT_APP_API_URL;

/** Map detectCardBrand (lowercase) to cardLogos keys (Visa, Mastercard, JCB, AMEX) */
const CARD_BRAND_TO_LOGO_KEY = {
  visa: "Visa",
  mastercard: "Mastercard",
  jcb: "JCB",
  amex: "AMEX",
};

/** Fallback for unknown brand (e.g. card preview) */
const CardBrandLogoUnknown = () => (
  <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
    <CreditCard className="w-5 h-5 text-gray-400" />
  </div>
);

export default function PaymentInstructionCard({
  amount,
  orderId,
  merchantName,
  expired_at,
  selected_method,
  onCheckStatus,
  bankName,
  bankCode,
}) {
  const [formData, setFormData] = useState({
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    cardHolderName: "",
    cardEmail: "",
    address: "",
    city: "",
    state: "",
    postalCode: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [cardBrand, setCardBrand] = useState("unknown");
  const [showCvv, setShowCvv] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState(null);
  const [show3DSModal, setShow3DSModal] = useState(false);
  const [threeDSFormHtml, setThreeDSFormHtml] = useState(null);
  // Midtrans returns a plain redirect URL instead of an HTML document.
  // We keep these as separate state slots so the mount effect can branch
  // on which one is set, mirroring the new payment-linking ThreeDSModal.
  const [threeDSUrl, setThreeDSUrl] = useState(null);
  const [modalHeights, setModalHeights] = useState({ modal: 500, iframe: 420 });
  const threeDSIframeRef = useRef(null);
  const IFRAME_3DS_NAME = "payment-link-tds-iframe";

  // Dynamic 3DS modal/iframe height from window (no vh/viewport meta dependency)
  useEffect(() => {
    if (!show3DSModal) return;
    const updateHeights = () => {
      const modal = Math.round(window.innerHeight * 0.8);
      setModalHeights({ modal, iframe: Math.max(320, modal - 100) });
    };
    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, [show3DSModal]);

  useEffect(() => {
    const brand = detectCardBrand(formData.cardNumber);
    setCardBrand(brand);
  }, [formData.cardNumber]);

  // 3DS modal mount: branches on payload type.
  //   - Nicepay: backend returns a full <html>...</html> document inside
  //     `html_form`. We MUST write it via iframe.contentDocument.write so
  //     the document's <base>, scripts, and form action (mpgsAuth.do) are
  //     preserved. This keeps the same behavior the legacy flow relied on
  //     before vendor routing was introduced (see
  //     payment-link/docs/3DS-blank-iframe-analysis.md for why a Blob URL
  //     workaround broke Mastercard's callback).
  //   - Midtrans: backend returns a plain redirect URL. We assign it to
  //     iframe.src directly — Midtrans expects a top-level GET to its 3DS
  //     ACS, and an iframe load works the same as the new-PL ThreeDSModal
  //     does for cross-vendor 3DS.
  // The two paths are exclusive: handlePay clears one and sets the other.
  useEffect(() => {
    if (!show3DSModal) return;
    if (!threeDSFormHtml && !threeDSUrl) return;
    const html = threeDSFormHtml;
    const url = threeDSUrl;
    const run = () => {
      const iframe = threeDSIframeRef.current;
      if (!iframe) return;
      if (html) {
        if (!iframe.contentDocument) return;
        try {
          iframe.contentDocument.open();
          iframe.contentDocument.write(html);
          iframe.contentDocument.close();
          setTimeout(() => {
            try {
              const form = iframe.contentDocument.querySelector("form[id='mpgsAuthFrm']") || iframe.contentDocument.querySelector("form");
              if (form) form.submit();
            } catch (_) {}
          }, 100);
        } catch (_) {}
        setThreeDSFormHtml(null);
        return;
      }
      // URL path (Midtrans).
      try {
        iframe.src = url;
      } catch (_) {}
      setThreeDSUrl(null);
    };
    const id = setTimeout(run, 50);
    return () => clearTimeout(id);
  }, [show3DSModal, threeDSFormHtml, threeDSUrl]);

  // Listen for 3DS result (Nicepay result page in iframe posts message to parent)
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== "nicepay_3ds_result") return;
      setShow3DSModal(false);
      if (data.success) {
        onCheckStatus?.();
      } else {
        setPayError(data.resultMessage || "3DS verification failed.");
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onCheckStatus]);

  // Status polling fallback while the 3DS modal is open.
  //
  // Nicepay's 3DS result page postMessages the parent on completion
  // (handled above) so we don't strictly need this for Nicepay — but
  // Midtrans's hosted 3DS finish page does NOT postMessage. Without a
  // poll the iframe stalls on Midtrans's "Transaction is pending. Tap
  // to Continue." fallback page indefinitely, even after the BE
  // webhook has already settled the PaymentLinkHistory row.
  //
  // Calling `onCheckStatus()` periodically defers terminal-state
  // detection to the parent (`ThirdForm.checkTransactionStatus`),
  // which on `paid` triggers a navigate-away — unmounting this whole
  // tree (and the modal with it). The interval is a gentle 4s; the
  // iframe-onLoad below provides faster detection on iframe
  // navigations (which Midtrans fires when it lands on the finish
  // page).
  // The parent's `checkTransactionStatus` returns the resolved row
  // status. We close the modal on `failed` here because the parent
  // does not navigate-away on failure (only on `paid` / `expired`),
  // so without a local close the modal would otherwise stay up after
  // a 3DS decline.
  useEffect(() => {
    if (!show3DSModal) return;
    if (!onCheckStatus) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await onCheckStatus();
        if (cancelled) return;
        if (status === "failed") {
          setShow3DSModal(false);
          setPayError("Verifikasi 3DS gagal. Silakan coba lagi.");
        }
      } catch (_) {}
    };
    const id = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [show3DSModal, onCheckStatus]);

  const handleChange = (field, value) => {
    let formattedValue = value;
    if (field === "cardNumber") {
      formattedValue = formatCardNumber(value);
    } else if (field === "cardExpiry") {
      formattedValue = formatExpiry(value);
    } else if (field === "cardCvv") {
      formattedValue = value.replace(/\D/g, "").slice(0, cardBrand === "amex" ? 4 : 3);
    } else if (field === "cardHolderName") {
      formattedValue = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = (field) => {
    const newErrors = { ...errors };
    switch (field) {
      case "cardNumber": {
        const num = formData.cardNumber;
        const brand = detectCardBrand(num);
        if (!num || !num.replace(/\s/g, "")) {
          delete newErrors.cardNumber;
        } else if (!isCardBrandSupported(brand)) {
          newErrors.cardNumber = "We only accept Visa, Mastercard, JCB, American Express, and China UnionPay";
        } else if (!validateCardNumber(num)) {
          newErrors.cardNumber = "Invalid card number";
        } else {
          delete newErrors.cardNumber;
        }
        break;
      }
      case "cardExpiry":
        if (formData.cardExpiry && !validateExpiry(formData.cardExpiry)) {
          newErrors.cardExpiry = "Invalid";
        } else {
          delete newErrors.cardExpiry;
        }
        break;
      case "cardCvv":
        if (formData.cardCvv && !validateCVV(formData.cardCvv, cardBrand)) {
          newErrors.cardCvv = `${cardBrand === "amex" ? "4" : "3"} digits`;
        } else {
          delete newErrors.cardCvv;
        }
        break;
      case "cardHolderName":
        if (formData.cardHolderName && formData.cardHolderName.length < 2) {
          newErrors.cardHolderName = "Name too short";
        } else {
          delete newErrors.cardHolderName;
        }
        break;
      case "cardEmail":
        if (formData.cardEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardEmail)) {
          newErrors.cardEmail = "Invalid email";
        } else {
          delete newErrors.cardEmail;
        }
        break;
      case "address":
        if (!formData.address || !formData.address.trim()) {
          newErrors.address = "Required";
        } else {
          delete newErrors.address;
        }
        break;
      case "city":
        if (!formData.city || !formData.city.trim()) {
          newErrors.city = "Required";
        } else {
          delete newErrors.city;
        }
        break;
      case "state":
        if (!formData.state || !formData.state.trim()) {
          newErrors.state = "Required";
        } else {
          delete newErrors.state;
        }
        break;
      case "postalCode":
        if (!formData.postalCode || !formData.postalCode.trim()) {
          newErrors.postalCode = "Required";
        } else if (!/^[\d\s\-a-zA-Z]{1,10}$/.test(formData.postalCode.trim())) {
          newErrors.postalCode = "Invalid postal code";
        } else {
          delete newErrors.postalCode;
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const hasError = (field) => errors[field] && touched[field];

  const inputBaseClass = (field) =>
    `w-full h-12 px-4 border-2 rounded-lg text-base transition-all duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
      hasError(field)
        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
        : "border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-100"
    }`;

  const validateAll = useCallback(() => {
    const fields = ["cardNumber", "cardExpiry", "cardCvv", "cardHolderName", "cardEmail", "address", "city", "state", "postalCode"];
    const newErrors = {};
    const detectedBrand = detectCardBrand(formData.cardNumber);
    if (!formData.cardNumber || !formData.cardNumber.replace(/\s/g, "")) {
      newErrors.cardNumber = "Invalid card number";
    } else if (!isCardBrandSupported(detectedBrand)) {
      newErrors.cardNumber = "We only accept Visa, Mastercard, JCB, American Express, and China UnionPay";
    } else if (!validateCardNumber(formData.cardNumber)) {
      newErrors.cardNumber = "Invalid card number";
    }
    if (!formData.cardExpiry || !validateExpiry(formData.cardExpiry)) {
      newErrors.cardExpiry = "Invalid";
    }
    if (!formData.cardCvv || !validateCVV(formData.cardCvv, cardBrand)) {
      newErrors.cardCvv = cardBrand === "amex" ? "4 digits" : "3 digits";
    }
    if (!formData.cardHolderName || formData.cardHolderName.trim().length < 2) {
      newErrors.cardHolderName = "Name too short";
    }
    if (!formData.cardEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardEmail)) {
      newErrors.cardEmail = "Invalid email";
    }
    if (!formData.address || !formData.address.trim()) {
      newErrors.address = "Required";
    }
    if (!formData.city || !formData.city.trim()) {
      newErrors.city = "Required";
    }
    if (!formData.state || !formData.state.trim()) {
      newErrors.state = "Required";
    }
    if (!formData.postalCode || !formData.postalCode.trim()) {
      newErrors.postalCode = "Required";
    } else if (!/^[\d\s\-a-zA-Z]{1,10}$/.test(formData.postalCode.trim())) {
      newErrors.postalCode = "Invalid postal code";
    }
    setErrors(newErrors);
    setTouched((prev) => {
      const next = { ...prev };
      fields.forEach((f) => { next[f] = true; });
      return next;
    });
    return Object.keys(newErrors).length === 0;
  }, [formData, cardBrand]);

  const handlePay = useCallback(async (e) => {
    e?.preventDefault();
    setPayError(null);
    if (!validateAll()) return;
    setPayLoading(true);
    try {
      const cardExpiryYyMm = parseExpiryToYyMm(formData.cardExpiry); // UI is MM/YY, send YYMM to API
      const res = await fetch(`${BASE_URL}/api/payment-link/card-payment`, {
        method: "POST",
        headers: getPaymentLinkHeaders(),
        body: JSON.stringify({
          reff_no: orderId,
          card_number: formData.cardNumber.replace(/\s/g, ""),
          card_expiry: cardExpiryYyMm,
          card_cvv: formData.cardCvv,
          card_holder_name: formData.cardHolderName.trim(),
          card_holder_email: formData.cardEmail.trim(),
          customer_address: formData.address.trim() || undefined,
          customer_city: formData.city.trim() || undefined,
          customer_state: formData.state.trim() || undefined,
          customer_postal_code: formData.postalCode.trim() || undefined,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPayError(data.message || "Payment failed.");
        return;
      }
      if (data.requires_3ds) {
        const htmlForm = data.data?.html_form;
        const paymentUrl = data.data?.payment_url;
        // Content-sniff the 3DS payload: a string starting with "<" is a
        // full HTML document (Nicepay); anything else is a redirect URL
        // (Midtrans). The mount effect's branching is driven by which
        // state slot we set here.
        if (typeof htmlForm === "string" && htmlForm.trim().startsWith("<")) {
          setThreeDSUrl(null);
          setThreeDSFormHtml(htmlForm);
          setShow3DSModal(true);
          return;
        }
        if (typeof paymentUrl === "string" && paymentUrl.trim() !== "") {
          setThreeDSFormHtml(null);
          setThreeDSUrl(paymentUrl);
          setShow3DSModal(true);
          return;
        }
        // Neither field present in a 3DS response — surface a generic
        // error rather than silently swallowing the response.
        setPayError("Unable to start 3DS verification. Please try again.");
        return;
      }
      onCheckStatus?.();
    } catch (err) {
      console.error("Card payment error:", err);
      setPayError(err.message || "Payment request failed.");
    } finally {
      setPayLoading(false);
    }
  }, [orderId, formData, validateAll, onCheckStatus]);

  const cardInstructions = [
    "1. Masukkan nomor kartu kredit/debit Anda",
    "2. Masukkan tanggal kadaluarsa kartu (MM/YY)",
    "3. Masukkan CVV (3 atau 4 digit di belakang kartu)",
    "4. Masukkan nama sesuai yang tertera di kartu",
    "5. Masukkan email untuk menerima konfirmasi pembayaran",
    "6. Klik tombol bayar dan ikuti langkah verifikasi jika diminta",
  ];

  return (
    <>
    <PaymentInstructionLayout
      amount={amount}
      orderId={orderId}
      merchantName={merchantName}
      expiredAt={expired_at}
      selectedMethod={selected_method}
      onCheckStatus={onCheckStatus}
      paymentCredential={
        <div className="mt-4 mb-6">
          {bankName && (
            <div className="flex items-center gap-2 justify-center mb-4">
              <CreditCard className="h-10 w-10 text-gray-700" aria-hidden />
              <h3 className="text-xl font-bold">{bankName}</h3>
            </div>
          )}
          <p className="text-gray-600 text-center mb-4">
            Masukkan detail kartu kredit/debit Anda untuk melanjutkan pembayaran.
          </p>

          {/* Card preview (same style as CreditCardForm) */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-5 text-white shadow-lg mb-6">
            <div className="flex justify-between items-start mb-8">
              <div className="text-xs uppercase tracking-wider text-gray-400">Card Details</div>
              {CARD_BRAND_TO_LOGO_KEY[cardBrand] && cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]] ? (
                <img
                  src={cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]]}
                  alt={cardBrand}
                  className="w-10 h-7 object-contain"
                />
              ) : (
                <CardBrandLogoUnknown />
              )}
            </div>
            <div className="font-mono text-xl tracking-widest mb-6">
              {formData.cardNumber || "•••• •••• •••• ••••"}
            </div>
            <div className="flex justify-between items-end">
              <div>
                <div className="text-[10px] uppercase text-gray-400 mb-1">Card Holder</div>
                <div className="text-sm font-medium tracking-wide">
                  {formData.cardHolderName || "YOUR NAME"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-gray-400 mb-1">Expires</div>
                <div className="text-sm font-medium">{formData.cardExpiry || "MM/YY"}</div>
              </div>
            </div>
          </div>

          {/* Card input fields (same layout/styling as CreditCardForm) */}
          <form className="space-y-4 max-w-md mx-auto">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Card Number <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2 shrink-0">
                  <img src={cardLogos.Visa} alt="Visa" className="w-8 h-5 object-contain" />
                  <img src={cardLogos.Mastercard} alt="Mastercard" className="w-8 h-5 object-contain" />
                  <img src={cardLogos.JCB} alt="JCB" className="w-8 h-5 object-contain" />
                  <img src={cardLogos.AMEX} alt="American Express" className="w-8 h-5 object-contain" />
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={formData.cardNumber}
                  onChange={(e) => handleChange("cardNumber", e.target.value)}
                  onBlur={() => handleBlur("cardNumber")}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className={`${inputBaseClass("cardNumber")} pr-14 font-mono`}
                />
                {CARD_BRAND_TO_LOGO_KEY[cardBrand] && cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]] && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-5 flex items-center justify-center">
                    <img
                      src={cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]]}
                      alt={cardBrand}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
              {hasError("cardNumber") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.cardNumber}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Expiry <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  value={formData.cardExpiry}
                  onChange={(e) => handleChange("cardExpiry", e.target.value)}
                  onBlur={() => handleBlur("cardExpiry")}
                  placeholder="MM/YY"
                  maxLength={5}
                  className={`${inputBaseClass("cardExpiry")} font-mono text-center`}
                />
                {hasError("cardExpiry") && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.cardExpiry}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  CVV <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showCvv ? "text" : "password"}
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    value={formData.cardCvv}
                    onChange={(e) => handleChange("cardCvv", e.target.value)}
                    onBlur={() => handleBlur("cardCvv")}
                    placeholder="•••"
                    maxLength={cardBrand === "amex" ? 4 : 3}
                    className={`${inputBaseClass("cardCvv")} pr-10 font-mono text-center`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCvv(!showCvv)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showCvv ? "Hide CVV" : "Show CVV"}
                  >
                    {showCvv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {hasError("cardCvv") && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.cardCvv}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Cardholder Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="cc-name"
                value={formData.cardHolderName}
                onChange={(e) => handleChange("cardHolderName", e.target.value)}
                onBlur={() => handleBlur("cardHolderName")}
                placeholder="JOHN DOE"
                className={`${inputBaseClass("cardHolderName")} uppercase tracking-wide`}
              />
              {hasError("cardHolderName") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.cardHolderName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                autoComplete="email"
                value={formData.cardEmail}
                onChange={(e) => handleChange("cardEmail", e.target.value)}
                onBlur={() => handleBlur("cardEmail")}
                placeholder="email@example.com"
                className={inputBaseClass("cardEmail")}
              />
              <p className="text-xs text-gray-500 mt-1">
                Email untuk konfirmasi pembayaran
              </p>
              {hasError("cardEmail") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.cardEmail}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Billing Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="street-address"
                value={formData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                onBlur={() => handleBlur("address")}
                placeholder="Street address"
                className={inputBaseClass("address")}
              />
              {hasError("address") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.address}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="address-level2"
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  onBlur={() => handleBlur("city")}
                  placeholder="City"
                  className={inputBaseClass("city")}
                />
                {hasError("city") && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.city}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  State / Province <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="address-level1"
                  value={formData.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  onBlur={() => handleBlur("state")}
                  placeholder="State or province"
                  className={inputBaseClass("state")}
                />
                {hasError("state") && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.state}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Postal code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="postal-code"
                value={formData.postalCode}
                onChange={(e) => handleChange("postalCode", e.target.value)}
                onBlur={() => handleBlur("postalCode")}
                placeholder="Postal code"
                maxLength={10}
                className={inputBaseClass("postalCode")}
              />
              {hasError("postalCode") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.postalCode}
                </p>
              )}
            </div>
          </form>

          <div className="flex items-center gap-2 text-xs text-gray-500 mt-4 max-w-md mx-auto">
            <Lock className="w-4 h-4 text-green-600 shrink-0" />
            <span>Your payment info is encrypted and secure</span>
          </div>

          {payError && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2 max-w-md mx-auto">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {payError}
            </div>
          )}

          <div className="mt-6 max-w-md mx-auto">
            <button
              type="button"
              onClick={handlePay}
              disabled={payLoading}
              className="w-full py-4 px-6 rounded-lg text-lg font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-colors"
            >
              {payLoading ? "Processing..." : "Pay now"}
            </button>
          </div>
        </div>
      }
      howToPay={
        <div className="space-y-4 mt-6">
          <h3 className="text-xl font-bold">Cara Pembayaran</h3>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              {cardInstructions.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      }
    />

    {show3DSModal &&
      createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
          style={{ position: "fixed", inset: 0, zIndex: 2147483647, pointerEvents: "auto" }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tds-modal-title"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col" style={{ height: `${modalHeights.modal}px` }}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
              <h3 id="tds-modal-title" className="text-lg font-semibold text-gray-900">Complete verification</h3>
              <button
                type="button"
                onClick={() => setShow3DSModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-100 flex flex-col shrink-0" style={{ height: `${modalHeights.iframe}px` }}>
              <iframe
                ref={threeDSIframeRef}
                title="3DS verification"
                name={IFRAME_3DS_NAME}
                className="w-full border-0"
                style={{ height: `${modalHeights.iframe}px` }}
                sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation"
                onLoad={async () => {
                  // Cross-origin restrictions stop us reading the
                  // iframe's URL when it's on a vendor domain, but the
                  // load event still fires on every navigation. Each
                  // such event is a strong hint that 3DS has progressed
                  // (e.g. Midtrans landing on its "Tap to Continue"
                  // page after the issuer ACS completes), so we probe
                  // the BE for terminal state immediately rather than
                  // waiting for the next 4s polling tick.
                  try {
                    const status = await onCheckStatus?.();
                    if (status === "failed") {
                      setShow3DSModal(false);
                      setPayError("Verifikasi 3DS gagal. Silakan coba lagi.");
                    }
                  } catch (_) {}
                }}
              />
            </div>
            <p className="px-4 py-2 text-sm text-gray-500 text-center border-t border-gray-100">
              Complete the verification in the window above. Do not close this dialog until finished.
            </p>
          </div>
        </div>,
        document.getElementById("payment-link-modal-root") || document.body
      )}
    </>
  );
}
